import { AppointmentHasuraStatus } from '../../../const/appointment';
import { ReactComponent as Info } from 'assets/icons/info-gray.svg';
import { ReactComponent as Warning } from 'assets/icons/warning.svg';
import { twMerge } from 'tailwind-merge';
import React, { useMemo } from 'react';
import { useAuth } from 'auth/context/AuthContext';
import { Roles } from '../../../../../firebase/interfaces';

export const STATUS_CLASS = {
  [AppointmentHasuraStatus.PendingProviderApproval]:
    '!text-amber-border border-amber-border bg-amber-50',
  [AppointmentHasuraStatus.Booked]:
    '!text-clc-blue border-clc-blue bg-positive',
  [AppointmentHasuraStatus.NotPaidFailedAuthorization]: '',
  [AppointmentHasuraStatus.NotPaidFailedWithdrawal]: '',
  [AppointmentHasuraStatus.Canceled]: 'bg-base-content',
  [AppointmentHasuraStatus.Completed]:
    '!text-bright-green border-bright-green bg-green-50',
  [AppointmentHasuraStatus.PatientNotShowingUp]: '',
  [AppointmentHasuraStatus.ProviderNotShowingUp]: '',
  [AppointmentHasuraStatus.NobodyNotShowingUp]: '',
  [AppointmentHasuraStatus.PendingAuthorizedPayment]:
    '!text-clc-red border-clc-red bg-red-50',
};

const PATIENT_STATUS_CLASS = {
  ...STATUS_CLASS,
  [AppointmentHasuraStatus.PendingCapturePayment]:
    'text-amber-border border-amber-border bg-amber-50',
  [AppointmentHasuraStatus.PendingAuthorizedPayment]:
    '!text-purple-700 border-purple-700 bg-purple-50',
};
const PROVIDER_STATUS_CLASS = {
  ...STATUS_CLASS,
  [AppointmentHasuraStatus.PendingCapturePayment]:
    '!text-clc-blue border-clc-blue bg-positive',
  [AppointmentHasuraStatus.PendingAuthorizedPayment]:
    '!text-amber-border border-amber-border bg-amber-50',
  [AppointmentHasuraStatus.NotPaidFailedAuthorization]:
    '!text-amber-border border-amber-border bg-amber-50',
  [AppointmentHasuraStatus.NotPaidFailedWithdrawal]:
    '!text-amber-border border-amber-border bg-amber-50',
};

const NOTIFICATION_STATUS_CLASS = {
  [AppointmentHasuraStatus.PendingProviderApproval]: 'text-amber-border',
  [AppointmentHasuraStatus.PendingCapturePayment]: 'text-amber-border',
  [AppointmentHasuraStatus.Booked]: '',
  [AppointmentHasuraStatus.NotPaidFailedAuthorization]: 'text-clc-red',
  [AppointmentHasuraStatus.NotPaidFailedWithdrawal]: 'text-clc-red',
  [AppointmentHasuraStatus.Canceled]: '',
  [AppointmentHasuraStatus.Completed]: 'text-bright-green',
  [AppointmentHasuraStatus.PatientNotShowingUp]: '',
  [AppointmentHasuraStatus.ProviderNotShowingUp]: '',
  [AppointmentHasuraStatus.NobodyNotShowingUp]: '',
  [AppointmentHasuraStatus.PendingAuthorizedPayment]: 'text-purple-700',
};

const NOTIFICATION_STATUS_CLASS_PROVIDER = {
  ...NOTIFICATION_STATUS_CLASS,
  [AppointmentHasuraStatus.PendingAuthorizedPayment]: 'text-amber-border',
  [AppointmentHasuraStatus.NotPaidFailedAuthorization]: 'text-amber-border',
  [AppointmentHasuraStatus.NotPaidFailedWithdrawal]: 'text-amber-border',
};

const ICON_CLASSNAMES = {
  [AppointmentHasuraStatus.PendingProviderApproval]: 'fill-amber-border',
  [AppointmentHasuraStatus.PendingCapturePayment]: 'fill-amber-border',
  [AppointmentHasuraStatus.NotPaidFailedAuthorization]: 'fill-alert-negative',
  [AppointmentHasuraStatus.NotPaidFailedWithdrawal]: 'fill-alert-negative',
  [AppointmentHasuraStatus.Canceled]: '',
  [AppointmentHasuraStatus.Completed]: '',
  [AppointmentHasuraStatus.PatientNotShowingUp]: '',
  [AppointmentHasuraStatus.ProviderNotShowingUp]: '',
  [AppointmentHasuraStatus.NobodyNotShowingUp]: '',
  [AppointmentHasuraStatus.Booked]: '',
};

const PATIENT_ICON_CLASSNAMES = {
  ...ICON_CLASSNAMES,
  [AppointmentHasuraStatus.PendingAuthorizedPayment]: 'fill-purple-700',
};

const PROVIDER_ICON_CLASSNAMES = {
  ...ICON_CLASSNAMES,
  [AppointmentHasuraStatus.PendingAuthorizedPayment]: 'fill-amber-border',
  [AppointmentHasuraStatus.NotPaidFailedAuthorization]: 'fill-amber-border',
  [AppointmentHasuraStatus.NotPaidFailedWithdrawal]: 'fill-amber-border',
};

const STATUS_NOTIFICATION_ICON = {
  [AppointmentHasuraStatus.PendingProviderApproval]: Info,
  [AppointmentHasuraStatus.PendingCapturePayment]: Info,
  [AppointmentHasuraStatus.Booked]: null,
  [AppointmentHasuraStatus.NotPaidFailedAuthorization]: Warning,
  [AppointmentHasuraStatus.NotPaidFailedWithdrawal]: Warning,
  [AppointmentHasuraStatus.Canceled]: null,
  [AppointmentHasuraStatus.Completed]: null,
  [AppointmentHasuraStatus.PatientNotShowingUp]: null,
  [AppointmentHasuraStatus.ProviderNotShowingUp]: null,
  [AppointmentHasuraStatus.NobodyNotShowingUp]: null,
};

const PATIENT_STATUS_NOTIFICATION_ICON = {
  ...STATUS_NOTIFICATION_ICON,
  [AppointmentHasuraStatus.PendingAuthorizedPayment]: Info,
};

const PROVIDER_STATUS_NOTIFICATION_ICON = {
  ...STATUS_NOTIFICATION_ICON,
  [AppointmentHasuraStatus.PendingAuthorizedPayment]: Info,
  [AppointmentHasuraStatus.NotPaidFailedAuthorization]: Info,
  [AppointmentHasuraStatus.NotPaidFailedWithdrawal]: Info,
};

export const PAID_STATUSES = [
  AppointmentHasuraStatus.NotPaidFailedAuthorization,
  AppointmentHasuraStatus.NotPaidFailedWithdrawal,
];

export interface IUseAppointmentStatusOutput {
  Icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>> | null;
  iconClassNames: string;
  notificationClassNames: string;
  notification: string;
  statusClassName: string;
  label: string;
}

export const useAppointmentHasuraStatus = (
  status: AppointmentHasuraStatus,
): IUseAppointmentStatusOutput => {
  const { user } = useAuth();

  const isProvider = useMemo(() => user?.role === Roles.PROVIDER, [user]);

  const statusClassName = useMemo(() => {
    const baseClass =
      'text-sm font-semibold border px-3 min-w-[6rem] text-center rounded-full';
    const statuses = isProvider ? PROVIDER_STATUS_CLASS : PATIENT_STATUS_CLASS;
    const className = statuses[status];
    return `${baseClass} ${className}`;
  }, [isProvider, status]);

  const STATUS_LABEL_STATIC = {
    [AppointmentHasuraStatus.Canceled]: 'canceled',
    [AppointmentHasuraStatus.Completed]: 'completed',
    [AppointmentHasuraStatus.Completed]: 'completed',
    [AppointmentHasuraStatus.PatientNotShowingUp]: 'Patient missed',
    [AppointmentHasuraStatus.ProviderNotShowingUp]: 'Provider missed',
    [AppointmentHasuraStatus.NobodyNotShowingUp]: 'noshow',
  };

  const STATUS_LABEL_PATIENT = {
    ...STATUS_LABEL_STATIC,
    [AppointmentHasuraStatus.PendingProviderApproval]: 'pending',
    [AppointmentHasuraStatus.PendingCapturePayment]: 'pending',
    [AppointmentHasuraStatus.Booked]: 'upcoming',
    [AppointmentHasuraStatus.NotPaidFailedAuthorization]: 'Booked. Not paid',
    [AppointmentHasuraStatus.NotPaidFailedWithdrawal]: 'Booked. Not paid',
    [AppointmentHasuraStatus.PendingAuthorizedPayment]: 'Pending payment',
  };

  const STATUS_LABEL_PROVIDER = {
    ...STATUS_LABEL_STATIC,
    [AppointmentHasuraStatus.PendingProviderApproval]: 'pending',
    [AppointmentHasuraStatus.PendingCapturePayment]: 'upcoming',
    [AppointmentHasuraStatus.Booked]: 'upcoming',
    [AppointmentHasuraStatus.NotPaidFailedAuthorization]: 'pending',
    [AppointmentHasuraStatus.NotPaidFailedWithdrawal]: 'pending',
    [AppointmentHasuraStatus.PendingAuthorizedPayment]: 'pending',
  };

  const STATUS_NOTIFICATION_PATIENT = {
    [AppointmentHasuraStatus.PendingProviderApproval]:
      'Waiting for provider’s approval',
    [AppointmentHasuraStatus.PendingCapturePayment]:
      'Waiting for payment. The payment will be charged 48 hours before appointment',
    [AppointmentHasuraStatus.Booked]: '',
    [AppointmentHasuraStatus.NotPaidFailedAuthorization]:
      'Payment failed. Please retry the payment to avoid cancellation of the appointment',
    [AppointmentHasuraStatus.NotPaidFailedWithdrawal]:
      'Payment failed. Please retry the payment to avoid cancellation of the appointment',
    [AppointmentHasuraStatus.Canceled]: '',
    [AppointmentHasuraStatus.Completed]: '',
    [AppointmentHasuraStatus.PatientNotShowingUp]: '',
    [AppointmentHasuraStatus.ProviderNotShowingUp]: '',
    [AppointmentHasuraStatus.NobodyNotShowingUp]: '',
    [AppointmentHasuraStatus.PendingAuthorizedPayment]:
      'Payment processing may take up to 3 minutes',
  };

  const STATUS_NOTIFICATION_PROVIDER = {
    [AppointmentHasuraStatus.PendingProviderApproval]:
      'Waiting for your approval',
    [AppointmentHasuraStatus.PendingCapturePayment]: '',
    [AppointmentHasuraStatus.Booked]: '',
    [AppointmentHasuraStatus.NotPaidFailedAuthorization]: 'Payment issue',
    [AppointmentHasuraStatus.NotPaidFailedWithdrawal]: 'Payment issue',
    [AppointmentHasuraStatus.Canceled]: '',
    [AppointmentHasuraStatus.Completed]: '',
    [AppointmentHasuraStatus.PatientNotShowingUp]: '',
    [AppointmentHasuraStatus.ProviderNotShowingUp]: '',
    [AppointmentHasuraStatus.NobodyNotShowingUp]: '',
    [AppointmentHasuraStatus.PendingAuthorizedPayment]: 'Waiting for payment',
  };

  const Icon = useMemo(() => {
    const icons = isProvider
      ? PROVIDER_STATUS_NOTIFICATION_ICON
      : PATIENT_STATUS_NOTIFICATION_ICON;
    return icons[status];
  }, [isProvider, status]);

  const iconClassNames = useMemo(() => {
    const statuses = isProvider
      ? PROVIDER_ICON_CLASSNAMES
      : PATIENT_ICON_CLASSNAMES;
    const className = statuses[status];
    return twMerge(className, 'w-[20px] h-[20px] mr-2');
  }, [isProvider, status]);

  const notificationClassNames = twMerge(
    (isProvider
      ? NOTIFICATION_STATUS_CLASS_PROVIDER
      : NOTIFICATION_STATUS_CLASS)[status],
    'text-sm font-semibold',
  );

  return {
    label: (isProvider ? STATUS_LABEL_PROVIDER : STATUS_LABEL_PATIENT)[status],
    notification: (isProvider
      ? STATUS_NOTIFICATION_PROVIDER
      : STATUS_NOTIFICATION_PATIENT)[status],
    Icon,
    iconClassNames,
    statusClassName,
    notificationClassNames,
  };
};
